import { useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Typography } from "antd";
import { HistoryOutlined } from "@ant-design/icons";
import { formatPacificDateTime, getAssignmentTimes } from "./assignmentTimes";

dayjs.extend(relativeTime);

const { Text } = Typography;

// Design Tokens with distinct colors for each section
const SECTION_COLORS = {
  saleMade:   { accent: "#722ED1", bg: "#F9F0FF", text: "#531DAB", iconBg: "#531DAB20", order: 1 },
  system:     { accent: "#378ADD", bg: "#E6F4FF", text: "#1677FF", iconBg: "#1677FF20", order: 2 },
  lead:       { accent: "#639922", bg: "#F6FFED", text: "#389E0D", iconBg: "#389E0D20", order: 3 },
  payment:    { accent: "#D4537E", bg: "#FFF0F6", text: "#C41D7F", iconBg: "#C41D7F20", order: 4 },
  assignment: { accent: "#7F77DD", bg: "#F4F0FF", text: "#531DAB", iconBg: "#531DAB20", order: 5 },
  warranty:   { accent: "#BA7517", bg: "#FFF7E6", text: "#D48806", iconBg: "#D4880620", order: 6 },
  zoho:       { accent: "#378ADD", bg: "#E6F4FF", text: "#1677FF", iconBg: "#1677FF20", order: 7 },
  invoice:    { accent: "#639922", bg: "#F6FFED", text: "#389E0D", iconBg: "#389E0D20", order: 8 },
  po:         { accent: "#7F77DD", bg: "#F4F0FF", text: "#531DAB", iconBg: "#531DAB20", order: 9 },
  docAllow:   { accent: "#13C2C2", bg: "#E6FFFB", text: "#08979C", iconBg: "#08979C20", order: 10 },
  payAllow:   { accent: "#D4537E", bg: "#FFF0F6", text: "#C41D7F", iconBg: "#C41D7F20", order: 11 },
  shipment:   { accent: "#378ADD", bg: "#E6F4FF", text: "#1677FF", iconBg: "#1677FF20", order: 12 },
  order:      { accent: "#639922", bg: "#F6FFED", text: "#389E0D", iconBg: "#389E0D20", order: 13 },
  yardRefund: { accent: "#BA7517", bg: "#FFF7E6", text: "#D48806", iconBg: "#D4880620", order: 14 },
  custRefund: { accent: "#D4537E", bg: "#FFF0F6", text: "#C41D7F", iconBg: "#C41D7F20", order: 15 },
  history:    { accent: "#BA7517", bg: "#FFF7E6", text: "#D48806", iconBg: "#D4880620", order: 16 },
};

// Helper function to format date
const formatDateTime = (dateString) => {
  if (!dateString) return null;
  return dayjs(dateString).format("MM-DD-YYYY hh:mm:ss A");
};

// Helper function to get time difference
const getTimeAgo = (dateString) => {
  if (!dateString) return null;
  return dayjs(dateString).fromNow();
};

const isOverdue = (dateString) => dateString && dayjs(dateString).isBefore(dayjs());

const REVIEW_LEAD_RECOVERED_BY_PREFIX = "Recovered from Review Lead by";

const isAdminOnlyLeadStageHistoryEntry = (entry = {}) => (
  entry.isAdminOnly === true ||
  (typeof entry.comment === "string" && entry.comment.trim().startsWith(REVIEW_LEAD_RECOVERED_BY_PREFIX))
);

const getVisibleLeadStageHistory = (history = [], isAdmin = false) => {
  const items = Array.isArray(history) ? history : [];
  return isAdmin ? items : items.filter((entry) => !isAdminOnlyLeadStageHistoryEntry(entry));
};

// Helper to format value display
const formatValue = (value, defaultValue = "Not Updated Yet") => {
  if (value === null || value === undefined || value === "") return defaultValue;
  if (typeof value === "string" && value.trim() === "") return defaultValue;
  return value;
};

// ─── Primitive Components ─────────────────────────────────────────────────────
const Badge = ({ children, color, bg, style = {} }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 10px",
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 500,
      background: bg || "#f0f0f0",
      color: color || "#666",
      ...style,
    }}
  >
    {children}
  </span>
);

const Pill = ({ value, type = "neutral" }) => {
  const displayValue = formatValue(value, "—");
  const map = {
    success: { bg: "#F6FFED", color: "#389E0D" },
    danger: { bg: "#FFF1F0", color: "#F5222D" },
    warning: { bg: "#FFF7E6", color: "#D48806" },
    info: { bg: "#E6F4FF", color: "#1677FF" },
    neutral: { bg: "#F5F5F5", color: "#8C8C8C" },
  };
  const t = map[type] || map.neutral;
  return <Badge color={t.color} bg={t.bg}>{displayValue}</Badge>;
};

const Timestamp = ({ date, overdue }) => {
  if (!date) return <span style={{ color: "#999", fontSize: 12 }}>Not Updated Yet</span>;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span style={{ fontSize: 12, color: overdue ? "#F5222D" : "#333", fontFamily: "monospace" }}>
        {formatDateTime(date)}
      </span>
      <Badge color={overdue ? "#F5222D" : "#999"} bg={overdue ? "#FFF1F0" : "#F5F5F5"}>
        {getTimeAgo(date)}
      </Badge>
    </div>
  );
};

// Same look as Timestamp, but always US Pacific (First Assigned / Last Transfer rows only).
// eslint-disable-next-line react/prop-types -- PropTypes are not used in this codebase
const PacificTimestamp = ({ date }) => {
  if (!date) return <span style={{ color: "#999", fontSize: 12 }}>Not Updated Yet</span>;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span style={{ fontSize: 12, color: "#333", fontFamily: "monospace" }}>
        {formatPacificDateTime(date)}
      </span>
      <Badge color="#999" bg="#F5F5F5">
        {getTimeAgo(date)}
      </Badge>
    </div>
  );
};

const EmptyState = ({ label }) => (
  <div style={{ padding: "24px", textAlign: "center", color: "#999", fontSize: 13, background: "#FAFAFA", borderRadius: 8 }}>
    {label || "No records found"}
  </div>
);

const Divider = ({ label, color }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0 16px" }}>
    <div style={{ flex: 1, height: 1, background: "#E8E8E8" }} />
    <span style={{ fontSize: 11, fontWeight: 600, color: color || "#666", letterSpacing: "0.5px", textTransform: "uppercase" }}>
      {label}
    </span>
    <div style={{ flex: 1, height: 1, background: "#E8E8E8" }} />
  </div>
);

// ─── Field Grid ───────────────────────────────────────────────────────────────
const FieldGrid = ({ fields, bgColor }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: 1,
      background: "#E8E8E8",
      border: "1px solid #E8E8E8",
      borderRadius: 8,
      overflow: "hidden",
    }}
  >
    {fields.map(({ label, value, span }) => (
      <div
        key={label}
        style={{
          background: bgColor || "#fff",
          padding: "12px 14px",
          gridColumn: span === 2 ? "1 / -1" : undefined,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: "#999",
            marginBottom: 6,
            fontWeight: 600,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
          }}
        >
          {label}
        </div>
        <div style={{ fontSize: 13 }}>{value}</div>
      </div>
    ))}
  </div>
);

// ─── Array Display Component ─────────────────────────────────────────────────
const ArrayDisplay = ({ items, title, color, renderItem, showCount = true }) => {
  if (!items || items.length === 0) return null;
  
  const sortedItems = [...items].sort((a, b) => {
    const dateA = a.changedAt || a.date || a.addedAt || a.yardConfirmDate || a.transferredAt;
    const dateB = b.changedAt || b.date || b.addedAt || b.yardConfirmDate || b.transferredAt;
    return dayjs(dateB).diff(dayjs(dateA));
  });

  return (
    <>
      {showCount && (
        <Divider label={`${title} · ${items.length} record(s)`} color={color} />
      )}
      {sortedItems.map((item, index) => (
        <div key={index} style={{ marginBottom: 16 }}>
          <Badge color={color} bg={`${color}15`} style={{ marginBottom: 8 }}>
            #{items.length - index}
          </Badge>
          {renderItem(item, index)}
        </div>
      ))}
    </>
  );
};

// ─── Section Accordion ────────────────────────────────────────────────────────
const Section = ({ id, title, icon, color, badge, children, openSections, onToggle }) => {
  const { accent, bg, text, iconBg } = color;
  const isOpen = openSections[id] || false;

  return (
    <div
      style={{
        border: `1px solid ${accent}30`,
        borderRadius: 12,
        overflow: "hidden",
        marginBottom: 16,
        background: "#fff",
        boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
      }}
    >
      <button
        onClick={() => onToggle(id)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 18px",
          background: bg || "#fff",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "all 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = `${accent}08`)}
        onMouseLeave={(e) => (e.currentTarget.style.background = bg || "#fff")}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: iconBg || `${accent}15`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            color: accent,
          }}
        >
          {icon}
        </div>
        <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", flex: 1 }}>{title}</span>
        {badge && (
          <Badge color={text} bg={`${accent}15`}>
            {badge}
          </Badge>
        )}
        <span
          style={{
            fontSize: 12,
            color: "#999",
            transform: isOpen ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
          }}
        >
          ▼
        </span>
      </button>
      {isOpen && (
        <div
          style={{
            padding: "16px 18px 20px",
            background: "#fff",
            borderTop: `1px solid ${accent}20`,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TrackingHistoryTab({ lead, userData }) {
  const [openSections, setOpenSections] = useState({
    systemDates: true,
    leadManagement: true,
    saleMade: true,
    warranty: true,
    zohoStatus: true,
    invoiceStatus: true,
    purchaseOrder: true,
    zohoDocAllow: true,
    paymentAllow: true,
    shipment: true,
    orderProcess: true,
    yardRefund: true,
    customerRefund: true,
    leadStageHistory: false,
    followUpHistory: false,
    quotedValues: false,
    quotes: false,
    advancePayments: false,
    yardConfirmations: false,
    transferHistory: false,
    zohoStatusHistory: false,
    invoiceStatusHistory: false,
    purchaseOrderHistory: false,
    zohoDocAllowHistory: false,
    paymentAllowHistory: false,
    shipmentHistory: false,
    orderHistory: false,
    yardRefundHistory: false,
    customerRefundHistory: false,
    saleMadeHistory: false,
  });

  const toggleSection = (id) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Get all arrays from lead data
  const isAdmin = userData?.role === "ADMIN";
  const leadStageHistory = getVisibleLeadStageHistory(lead?.leadStageHistory, isAdmin);
  const followUpHistory = [...(lead?.followUpHistory || [])];
  const quotedValues = [...(lead?.quotedValue || [])];
  const quotes = [...(lead?.quotes || [])];
  const advancePayments = [...(lead?.advancePayments || [])];
  const yardConfirmations = [...(lead?.yard || [])];
  const transferHistory = [...(lead?.transferHistory || [])];
  const zohoStatusHistory = [...(lead?.zohoStatusHistory || [])];
  const invoiceStatusHistory = [...(lead?.invoiceStatusHistory || [])];
  const purchaseOrderHistory = [...(lead?.purchaseOrderStatusHistory || [])];
  const zohoDocAllowHistory = [...(lead?.zohoDocAllowHistory || [])];
  const paymentAllowHistory = [...(lead?.payementAllowHistory || [])];
  const shipmentHistory = [...(lead?.ShipmentTrackingHistory || [])];
  const orderHistory = [...(lead?.orderProcessCompleteHistory || [])];
  const yardRefundHistory = [...(lead?.yardRefundHistory || [])];
  const customerRefundHistory = [...(lead?.customerRefundHistory || [])];
  const saleMadeHistory = [...(lead?.saleMadeChargeHistory || [])];

  // assignedDate is overwritten by every transfer, so show the first assignment and the latest transfer
  const assignmentTimes = getAssignmentTimes(lead);

  const shipmentData = lead?.ShipmentTracking || {};
  const orderData = lead?.orderProcessComplete || {};
  const yardData = lead?.yardRefund || {};
  const customerData = lead?.customerRefund || {};
  const saleData = lead?.saleMadeCharge || {};

  // Render functions for different array types
  const renderLeadStageItem = (item) => (
    <FieldGrid
      fields={[
        { label: "Stage", value: <Pill value={item.stage} type="info" /> },
        { label: "Changed At", value: <Timestamp date={item.changedAt} /> },
        { label: "Comment", value: <span style={{ fontSize: 13 }}>{formatValue(item.comment, "No comment")}</span>, span: 2 },
      ]}
      bgColor={SECTION_COLORS.lead.bg}
    />
  );

  const renderFollowUpItem = (item) => (
    <FieldGrid
      fields={[
        { label: "Scheduled Date", value: <Timestamp date={item.scheduledDate} /> },
        { label: "Completed Date", value: <Timestamp date={item.completedDate} /> },
        { label: "Status", value: <Pill value={item.status || "Pending"} type={item.completedDate ? "success" : "warning"} /> },
        { label: "Notes", value: <span style={{ fontSize: 13 }}>{formatValue(item.notes, "No notes")}</span>, span: 2 },
      ]}
      bgColor={SECTION_COLORS.lead.bg}
    />
  );

  const renderQuotedValueItem = (item) => (
    <FieldGrid
      fields={[
        { label: "Price", value: <span style={{ fontSize: 13, fontFamily: "monospace", fontWeight: 600 }}>${item.price}</span> },
        { label: "Added At", value: <Timestamp date={item.addedAt} /> },
      ]}
      bgColor={SECTION_COLORS.lead.bg}
    />
  );

  const renderQuoteItem = (item) => (
    <FieldGrid
      fields={[
        { label: "Quote ID", value: <span style={{ fontSize: 12, fontFamily: "monospace" }}>{formatValue(item.id, "N/A")}</span> },
        { label: "Date", value: <Timestamp date={item.date} /> },
        { label: "Amount", value: <span style={{ fontSize: 13, fontFamily: "monospace" }}>${item.amount}</span> },
        { label: "Status", value: <Pill value={item.status} type={item.status === "Approved" ? "success" : "neutral"} /> },
        { label: "Advance Payment", value: <span style={{ fontSize: 13, fontFamily: "monospace" }}>${item.advancePayment || 0}</span> },
        { label: "Description", value: <span style={{ fontSize: 13 }}>{formatValue(item.description, "No description")}</span>, span: 2 },
      ]}
      bgColor={SECTION_COLORS.lead.bg}
    />
  );

  const renderPaymentItem = (item) => (
    <FieldGrid
      fields={[
        { label: "Payment Date", value: <Timestamp date={item.date} /> },
        { label: "Amount", value: <span style={{ fontSize: 13, fontFamily: "monospace" }}>${(item.amount || 0).toFixed(2)}</span> },
        { label: "Payment Method", value: <span style={{ fontSize: 13 }}>{formatValue(item.paymentMethod, "Credit Card")}</span> },
        { label: "Transaction ID", value: <span style={{ fontSize: 12, fontFamily: "monospace" }}>{formatValue(item.id, "N/A")}</span> },
      ]}
      bgColor={SECTION_COLORS.payment.bg}
    />
  );

  const renderYardItem = (item) => (
    <FieldGrid
      fields={[
        { label: "Yard Confirm Date", value: <Timestamp date={item.yardConfirmDate} /> },
        { label: "Price", value: <span style={{ fontSize: 13, fontFamily: "monospace" }}>${item.price || 0}</span> },
        { label: "Original Price", value: <span style={{ fontSize: 13, fontFamily: "monospace" }}>${item.originalPrice || 0}</span> },
        { label: "Final Price", value: <span style={{ fontSize: 13, fontFamily: "monospace" }}>${item.finalPrice || 0}</span> },
        { label: "Description", value: <span style={{ fontSize: 13 }}>{formatValue(item.description, "No description")}</span>, span: 2 },
      ]}
      bgColor={SECTION_COLORS.history.bg}
    />
  );

  const renderTransferItem = (item) => (
    <FieldGrid
      fields={[
        { label: "Transfer Date", value: <Timestamp date={item.transferredAt} /> },
        { label: "From Agent", value: <span style={{ fontSize: 13 }}>{formatValue(item.fromAgent, "Unknown")}</span> },
        { label: "To Agent", value: <span style={{ fontSize: 13 }}>{formatValue(item.toAgent, "Unknown")}</span> },
        { label: "Reason", value: <span style={{ fontSize: 13 }}>{formatValue(item.reason, "Not specified")}</span>, span: 2 },
      ]}
      bgColor={SECTION_COLORS.assignment.bg}
    />
  );

  const renderStatusHistoryItem = (item, title) => (
    <FieldGrid
      fields={[
        { label: title, value: <Pill value={item.status || item.value} type="info" /> },
        { label: "Changed At", value: <Timestamp date={item.changedAt} /> },
      ]}
      bgColor={SECTION_COLORS.history.bg}
    />
  );

  const renderDateHistoryItem = (item) => (
    <FieldGrid
      fields={[
        { label: "Changed At", value: <Timestamp date={item.changedAt} /> },
      ]}
      bgColor={SECTION_COLORS.history.bg}
    />
  );

  const sections = [
    // Main date fields (always visible)
    {
      id: "systemDates",
      title: "📅 System Dates",
      icon: "⊙",
      color: SECTION_COLORS.system,
      content: () => (
        <FieldGrid
          fields={[
            { label: "Created At", value: <Timestamp date={lead?.createdAt} /> },
            { label: "Updated At", value: <Timestamp date={lead?.updatedAt} /> },
          ]}
          bgColor={SECTION_COLORS.system.bg}
        />
      ),
    },
    {
      id: "leadManagement",
      title: "📋 Lead Management",
      icon: "◈",
      color: SECTION_COLORS.lead,
      content: () => (
        <FieldGrid
          fields={[
            { label: "First Assigned (PT)", value: <PacificTimestamp date={assignmentTimes.firstAssignedAt} /> },
            ...(assignmentTimes.lastTransferAt
              ? [{ label: "Last Transfer (PT)", value: <PacificTimestamp date={assignmentTimes.lastTransferAt} /> }]
              : []),
            { label: "Order Date", value: <Timestamp date={lead?.orderDate} /> },
            { label: "Follow Up Date", value: <Timestamp date={lead?.followUpDate} overdue={isOverdue(lead?.followUpDate)} /> },
          ]}
          bgColor={SECTION_COLORS.lead.bg}
        />
      ),
    },
    {
      id: "saleMade",
      title: "📈 Sale Made Charge",
      icon: "↑",
      color: SECTION_COLORS.saleMade,
      content: () => (
        <>
          <FieldGrid
            fields={[
              { label: "Last Updated", value: <Timestamp date={lead?.saleMadeChargeUpdatedAt} /> },
            ]}
            bgColor={SECTION_COLORS.saleMade.bg}
          />
          {saleMadeHistory.length > 0 && (
            <ArrayDisplay 
              items={saleMadeHistory} 
              title="Sale Made Charge History" 
              color={SECTION_COLORS.saleMade.accent} 
              renderItem={renderDateHistoryItem}
            />
          )}
        </>
      ),
    },
    {
      id: "warranty",
      title: "🛡️ Warranty",
      icon: "◎",
      color: SECTION_COLORS.warranty,
      content: () => (
        <FieldGrid
          fields={[
            { label: "Last Updated", value: <Timestamp date={lead?.warrantyUpdatedAt} /> },
          ]}
          bgColor={SECTION_COLORS.warranty.bg}
        />
      ),
    },
    {
      id: "zohoStatus",
      title: "📧 Zoho Status",
      icon: "⬟",
      color: SECTION_COLORS.zoho,
      content: () => (
        <>
          <FieldGrid
            fields={[
              { label: "Last Updated", value: <Timestamp date={lead?.zohoStatusUpdatedAt} /> },
            ]}
            bgColor={SECTION_COLORS.zoho.bg}
          />
          {zohoStatusHistory.length > 0 && (
            <ArrayDisplay 
              items={zohoStatusHistory} 
              title="Zoho Status History" 
              color={SECTION_COLORS.zoho.accent} 
              renderItem={(item) => renderStatusHistoryItem(item, "Status")}
            />
          )}
        </>
      ),
    },
    {
      id: "invoiceStatus",
      title: "📄 Invoice Status",
      icon: "☰",
      color: SECTION_COLORS.invoice,
      content: () => (
        <>
          <FieldGrid
            fields={[
              { label: "Last Updated", value: <Timestamp date={lead?.invoiceStatusUpdatedAt} /> },
            ]}
            bgColor={SECTION_COLORS.invoice.bg}
          />
          {invoiceStatusHistory.length > 0 && (
            <ArrayDisplay 
              items={invoiceStatusHistory} 
              title="Invoice Status History" 
              color={SECTION_COLORS.invoice.accent} 
              renderItem={(item) => renderStatusHistoryItem(item, "Status")}
            />
          )}
        </>
      ),
    },
    {
      id: "purchaseOrder",
      title: "📦 Purchase Order",
      icon: "⊞",
      color: SECTION_COLORS.po,
      content: () => (
        <>
          <FieldGrid
            fields={[
              { label: "Last Updated", value: <Timestamp date={lead?.purchaseOrderStatusUpdatedAt} /> },
            ]}
            bgColor={SECTION_COLORS.po.bg}
          />
          {purchaseOrderHistory.length > 0 && (
            <ArrayDisplay 
              items={purchaseOrderHistory} 
              title="Purchase Order History" 
              color={SECTION_COLORS.po.accent} 
              renderItem={(item) => renderStatusHistoryItem(item, "Status")}
            />
          )}
        </>
      ),
    },
    {
      id: "zohoDocAllow",
      title: "📎 Zoho Doc Allow",
      icon: "✦",
      color: SECTION_COLORS.docAllow,
      content: () => (
        <>
          <FieldGrid
            fields={[
              { label: "Last Updated", value: <Timestamp date={lead?.zohoDocAllowUpdatedAt} /> },
            ]}
            bgColor={SECTION_COLORS.docAllow.bg}
          />
          {zohoDocAllowHistory.length > 0 && (
            <ArrayDisplay 
              items={zohoDocAllowHistory} 
              title="Zoho Doc Allow History" 
              color={SECTION_COLORS.docAllow.accent} 
              renderItem={(item) => renderStatusHistoryItem(item, "Value")}
            />
          )}
        </>
      ),
    },
    {
      id: "paymentAllow",
      title: "💳  Payment Paid",
      icon: "⬡",
      color: SECTION_COLORS.payAllow,
      content: () => (
        <>
          <FieldGrid
            fields={[
              { label: "Last Updated", value: <Timestamp date={lead?.payementAllowUpdatedAt} /> },
            ]}
            bgColor={SECTION_COLORS.payAllow.bg}
          />
          {paymentAllowHistory.length > 0 && (
            <ArrayDisplay 
              items={paymentAllowHistory} 
              title=" Payment Paid History" 
              color={SECTION_COLORS.payAllow.accent} 
              renderItem={(item) => renderStatusHistoryItem(item, "Value")}
            />
          )}
        </>
      ),
    },
    {
      id: "shipment",
      title: "🚚 Shipment Tracking",
      icon: "⊳",
      color: SECTION_COLORS.shipment,
      badge: shipmentData.trackingStatus || "Not Set",
      content: () => (
        <>
          <FieldGrid
            fields={[
              { label: "Last Updated", value: <Timestamp date={lead?.ShipmentTrackingUpdatedAt} /> },
            ]}
            bgColor={SECTION_COLORS.shipment.bg}
          />
          {shipmentHistory.length > 0 && (
            <ArrayDisplay 
              items={shipmentHistory} 
              title="Shipment Tracking History" 
              color={SECTION_COLORS.shipment.accent} 
              renderItem={renderDateHistoryItem}
            />
          )}
        </>
      ),
    },
    {
      id: "orderProcess",
      title: "✅ Order Process",
      icon: "✓",
      color: SECTION_COLORS.order,
      badge: orderData.deliveryStatus || "Not Delivered",
      content: () => (
        <>
          <FieldGrid
            fields={[
              { label: "Last Updated", value: <Timestamp date={lead?.orderProcessCompleteUpdatedAt} /> },
            ]}
            bgColor={SECTION_COLORS.order.bg}
          />
          {orderHistory.length > 0 && (
            <ArrayDisplay 
              items={orderHistory} 
              title="Order Process History" 
              color={SECTION_COLORS.order.accent} 
              renderItem={renderDateHistoryItem}
            />
          )}
        </>
      ),
    },
    {
      id: "yardRefund",
      title: "🏢 Yard Refund",
      icon: "↺",
      color: SECTION_COLORS.yardRefund,
      content: () => (
        <>
          <FieldGrid
            fields={[
              { label: "Last Updated", value: <Timestamp date={lead?.yardRefundUpdatedAt} /> },
            ]}
            bgColor={SECTION_COLORS.yardRefund.bg}
          />
          {yardRefundHistory.length > 0 && (
            <ArrayDisplay 
              items={yardRefundHistory} 
              title="Yard Refund History" 
              color={SECTION_COLORS.yardRefund.accent} 
              renderItem={renderDateHistoryItem}
            />
          )}
        </>
      ),
    },
    {
      id: "customerRefund",
      title: "👤 Customer Refund",
      icon: "⇐",
      color: SECTION_COLORS.custRefund,
      content: () => (
        <>
          <FieldGrid
            fields={[
              { label: "Last Updated", value: <Timestamp date={lead?.customerRefundUpdatedAt} /> },
            ]}
            bgColor={SECTION_COLORS.custRefund.bg}
          />
          {customerRefundHistory.length > 0 && (
            <ArrayDisplay 
              items={customerRefundHistory} 
              title="Customer Refund History" 
              color={SECTION_COLORS.custRefund.accent} 
              renderItem={renderDateHistoryItem}
            />
          )}
        </>
      ),
    },
    // Additional array sections
    {
      id: "leadStageHistory",
      title: "📜 Lead Stage History",
      icon: "◈",
      color: SECTION_COLORS.history,
      badge: `${leadStageHistory.length} change(s)`,
      content: () => (
        leadStageHistory.length === 0 ? 
          <EmptyState label="No lead stage history" /> :
          <ArrayDisplay items={leadStageHistory} title="Stage Changes" color={SECTION_COLORS.history.accent} renderItem={renderLeadStageItem} showCount={false} />
      ),
    },
    {
      id: "followUpHistory",
      title: "📞 Follow Up History",
      icon: "◈",
      color: SECTION_COLORS.history,
      badge: `${followUpHistory.length} follow-up(s)`,
      content: () => (
        followUpHistory.length === 0 ? 
          <EmptyState label="No follow up history" /> :
          <ArrayDisplay items={followUpHistory} title="Follow Ups" color={SECTION_COLORS.history.accent} renderItem={renderFollowUpItem} showCount={false} />
      ),
    },
    {
      id: "quotedValues",
      title: "💰 Quoted Values",
      icon: "◈",
      color: SECTION_COLORS.history,
      badge: `${quotedValues.length} quote(s)`,
      content: () => (
        quotedValues.length === 0 ? 
          <EmptyState label="No quoted values" /> :
          <ArrayDisplay items={quotedValues} title="Quotes" color={SECTION_COLORS.history.accent} renderItem={renderQuotedValueItem} showCount={false} />
      ),
    },
  
    {
      id: "advancePayments",
      title: "💰 Advance Payments",
      icon: "⬡",
      color: SECTION_COLORS.payment,
      badge: `${advancePayments.length} payment(s)`,
      content: () => (
        advancePayments.length === 0 ? 
          <EmptyState label="No advance payments" /> :
          <ArrayDisplay items={advancePayments} title="Payments" color={SECTION_COLORS.payment.accent} renderItem={renderPaymentItem} showCount={false} />
      ),
    },
    {
      id: "yardConfirmations",
      title: "🏢 Yard Confirmations",
      icon: "◈",
      color: SECTION_COLORS.history,
      badge: `${yardConfirmations.length} confirmation(s)`,
      content: () => (
        yardConfirmations.length === 0 ? 
          <EmptyState label="No yard confirmations" /> :
          <ArrayDisplay items={yardConfirmations} title="Yard Confirmations" color={SECTION_COLORS.history.accent} renderItem={renderYardItem} showCount={false} />
      ),
    },
    {
      id: "transferHistory",
      title: "🔄 Transfer History",
      icon: "⇄",
      color: SECTION_COLORS.assignment,
      badge: `${transferHistory.length} transfer(s)`,
      content: () => (
        transferHistory.length === 0 ? 
          <EmptyState label="No transfer history" /> :
          <ArrayDisplay items={transferHistory} title="Transfers" color={SECTION_COLORS.assignment.accent} renderItem={renderTransferItem} showCount={false} />
      ),
    },
  ];

  return (
    <div
      style={{
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto",
        maxWidth: 900,
        margin: "0 auto",
        padding: "0 4px",
      }}
    >
      {/* Header with Summary */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
          padding: "0 4px",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#1a1a1a" }}>
            <HistoryOutlined style={{ marginRight: 8, color: "#1677FF" }} />
            Tracking History
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#999" }}>
            Complete tracking information for this lead
          </p>
        </div>
        
        {/* Summary Badges */}
        
      </div>

      {/* Sections */}
      <div>
        {sections.map((section) => (
          <Section
            key={section.id}
            id={section.id}
            title={section.title}
            icon={section.icon}
            color={section.color}
            badge={section.badge}
            openSections={openSections}
            onToggle={toggleSection}
          >
            {section.content()}
          </Section>
        ))}
      </div>
    </div>
  );
}

